/**
 * Shared "seek the carousel to an index" helper.
 *
 * Two consumers need identical fit-aware navigation:
 *   - `autoplay` (its tick + per-slide hover seek)
 *   - `card-flip` (click-to-navigate)
 *
 * The behaviour depends on whether every slide fits in the viewport, decided
 * per call by `allSlidesFit`:
 *
 *   - All slides fit (numeric `slidesPerView >= total`, typical desktop): the
 *     parent can't translate, so `slideTo` would clamp to translate 0 and
 *     leave `activeIndex` put. Instead rotate the active/prev/next classes
 *     manually and push synced followers to the same index via the registry
 *     (no `slideChange` fires on a non-translating parent, so synced-swipers
 *     can't propagate for us here).
 *
 *   - Slides don't fit (typical mobile, or `slidesPerView: 'auto'`): call
 *     `slideTo`; Swiper translates, fires `slideChange`, and synced-swipers'
 *     own listener propagates to followers.
 *
 * Keeping this in one place means autoplay and card-flip can't drift in how
 * they move the carousel or which followers they drive.
 */

/**
 * `slidesPerView` may be a number ('1', '3') or the literal string 'auto'.
 * 'auto' routes to the slideTo path (Swiper translates naturally); only a
 * numeric SPV with `total <= spv` qualifies as "every slide fits".
 */
export function allSlidesFit(swiper) {
  const total = swiper.slides.length;
  const spv = swiper.params.slidesPerView;
  return typeof spv === 'number' && total <= spv;
}

/**
 * Rotate active/prev/next classes on a parent that doesn't translate, and
 * push the same index to synced followers via the shared registry.
 *
 * Followers are looked up by `data-sync-id` and filtered to those marked
 * `data-parent-swiper="false"`, mirroring synced-swipers' own follower filter.
 */
export function rotateClassesAndSyncFollowers(swiper, registry, syncId, newIndex) {
  swiper.slides.forEach((s) =>
    s.classList.remove('swiper-slide-active', 'swiper-slide-prev', 'swiper-slide-next')
  );
  const len = swiper.slides.length;
  swiper.slides[newIndex]?.classList.add('swiper-slide-active');
  const prev = newIndex === 0 ? len - 1 : newIndex - 1;
  const next = newIndex === len - 1 ? 0 : newIndex + 1;
  swiper.slides[prev]?.classList.add('swiper-slide-prev');
  swiper.slides[next]?.classList.add('swiper-slide-next');

  if (!syncId || !registry) return;
  for (const member of registry.getSyncGroup(syncId)) {
    if (member === swiper) continue;
    if (member.destroyed) continue;
    const memberSection = member.el.closest('.swiper-section');
    if (memberSection?.dataset?.parentSwiper !== 'false') continue;
    try {
      member.slideTo(newIndex, swiper.params.speed, true);
    } catch (err) {
      console.warn('seek: slideTo on follower failed', err);
    }
  }
}

/**
 * Move the carousel to `newIndex`, choosing the class-rotation or translate
 * strategy based on whether all slides fit. Safe to call on a destroyed
 * swiper (no-op).
 */
export function seekTo(swiper, registry, syncId, newIndex) {
  if (swiper.destroyed) return;
  if (allSlidesFit(swiper)) {
    rotateClassesAndSyncFollowers(swiper, registry, syncId, newIndex);
  } else {
    try {
      swiper.slideTo(newIndex, swiper.params.speed, true);
    } catch (err) {
      console.warn('seek: slideTo on parent failed', err);
    }
  }
}
